import React, { useState, useEffect, useCallback } from 'react';
import {
  listCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  CategoryResponse,
} from '../../api/admin';
import './Admin.css';

type SortField = 'skill_level' | 'name' | 'difficulty' | 'time_limit_seconds' | 'cards_per_session' | 'min_grade' | 'total_cards';
type SortDirection = 'asc' | 'desc';

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<CategoryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('skill_level');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Modal states
  const [modal, setModal] = useState<{ mode: 'create' | 'edit'; category?: CategoryResponse } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Form states
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [difficulty, setDifficulty] = useState(1);
  const [skillLevel, setSkillLevel] = useState(100);
  const [timeLimit, setTimeLimit] = useState(10);
  const [cardsPerSession, setCardsPerSession] = useState(20);
  const [minGrade, setMinGrade] = useState(0);
  const [maxGrade, setMaxGrade] = useState(5);

  // Difficulty options helper
  const difficultyOptions = [
    { value: 1, label: 'Easy' },
    { value: 2, label: 'Medium' },
    { value: 3, label: 'Hard' },
  ];

  const formatDifficulty = (diff: number): string => {
    const option = difficultyOptions.find(o => o.value === diff);
    return option ? option.label : `Level ${diff}`;
  };

  // Grade options helper
  const gradeOptions = [
    { value: 0, label: 'Kindergarten' },
    { value: 1, label: '1st Grade' },
    { value: 2, label: '2nd Grade' },
    { value: 3, label: '3rd Grade' },
    { value: 4, label: '4th Grade' },
    { value: 5, label: '5th Grade' },
  ];

  const formatGrade = (grade: number): string => {
    const option = gradeOptions.find(o => o.value === grade);
    return option ? option.label : `Grade ${grade}`;
  };

  // Sorting logic
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedCategories = [...categories].sort((a, b) => {
    let aVal: number | string = a[sortField] ?? 0;
    let bVal: number | string = b[sortField] ?? 0;

    // For name, use string comparison
    if (sortField === 'name') {
      aVal = (a.name || '').toLowerCase();
      bVal = (b.name || '').toLowerCase();
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const SortHeader: React.FC<{ field: SortField; label: string }> = ({ field, label }) => (
    <th
      onClick={() => handleSort(field)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
    >
      {label}
      {sortField === field && (
        <span style={{ marginLeft: '4px' }}>
          {sortDirection === 'asc' ? '▲' : '▼'}
        </span>
      )}
    </th>
  );

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listCategories();
      setCategories(data);
    } catch (err) {
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const openModal = (mode: 'create' | 'edit', category?: CategoryResponse) => {
    setModal({ mode, category });
    setName(category?.name || '');
    setDescription(category?.description || '');
    setDifficulty(category?.difficulty ?? 1);
    setSkillLevel(category?.skill_level ?? 100);
    setTimeLimit(category?.time_limit_seconds || 10);
    setCardsPerSession(category?.cards_per_session || 20);
    setMinGrade(category?.min_grade ?? 0);
    setMaxGrade(category?.max_grade ?? 5);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    if (minGrade > maxGrade) {
      setError('Min grade cannot be greater than max grade');
      return;
    }
    setSaving(true);
    try {
      if (modal?.mode === 'create') {
        await createCategory({
          name,
          description: description || undefined,
          difficulty,
          skill_level: skillLevel,
          time_limit_seconds: timeLimit,
          cards_per_session: cardsPerSession,
          min_grade: minGrade,
          max_grade: maxGrade,
        });
      } else if (modal?.category) {
        await updateCategory(modal.category.id, {
          name,
          description: description || undefined,
          difficulty,
          skill_level: skillLevel,
          time_limit_seconds: timeLimit,
          cards_per_session: cardsPerSession,
          min_grade: minGrade,
          max_grade: maxGrade,
        });
      }
      await fetchCategories();
      setModal(null);
    } catch (err) {
      setError('Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setSaving(true);
    try {
      await deleteCategory(deleteConfirm);
      await fetchCategories();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete category');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="admin-empty"><p>Loading...</p></div>;
  }

  return (
    <div>
      <div className="admin-page-header">
        <h2>Categories</h2>
        <button
          className="admin-btn admin-btn-primary"
          onClick={() => openModal('create')}
        >
          Add Category
        </button>
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      <div className="admin-table-container">
        {categories.length === 0 ? (
          <div className="admin-empty">
            <p>No categories yet</p>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <SortHeader field="skill_level" label="Skill Level" />
                <SortHeader field="name" label="Name" />
                <th>Description</th>
                <SortHeader field="difficulty" label="Difficulty" />
                <SortHeader field="time_limit_seconds" label="Time Limit (s)" />
                <SortHeader field="cards_per_session" label="Cards/Session" />
                <SortHeader field="min_grade" label="Grade Range" />
                <SortHeader field="total_cards" label="Total Cards" />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedCategories.map((cat) => (
                <tr key={cat.id}>
                  <td>{cat.skill_level}</td>
                  <td>{cat.name}</td>
                  <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {cat.description || '-'}
                  </td>
                  <td>{formatDifficulty(cat.difficulty)}</td>
                  <td>{cat.time_limit_seconds}</td>
                  <td>{cat.cards_per_session}</td>
                  <td>{`${formatGrade(cat.min_grade)} - ${formatGrade(cat.max_grade)}`}</td>
                  <td>{cat.total_cards}</td>
                  <td>
                    <div className="admin-btn-group">
                      <button
                        className="admin-btn admin-btn-secondary admin-btn-small"
                        onClick={() => openModal('edit', cat)}
                      >
                        Edit
                      </button>
                      <button
                        className="admin-btn admin-btn-danger admin-btn-small"
                        onClick={() => setDeleteConfirm(cat.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Category Modal */}
      {modal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <h3>{modal.mode === 'create' ? 'Add Category' : 'Edit Category'}</h3>
            <div className="admin-form-group">
              <label>Name</label>
              <input
                type="text"
                className="admin-form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Category name"
                maxLength={50}
              />
            </div>
            <div className="admin-form-group">
              <label>Description</label>
              <input
                type="text"
                className="admin-form-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description"
                maxLength={200}
              />
            </div>
            <div className="admin-form-group">
              <label>Difficulty</label>
              <select
                className="admin-form-input"
                value={difficulty}
                onChange={(e) => setDifficulty(parseInt(e.target.value))}
              >
                {difficultyOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="admin-form-group">
              <label>Skill Level (lower = unlocks earlier)</label>
              <input
                type="number"
                className="admin-form-input"
                value={skillLevel}
                onChange={(e) => setSkillLevel(parseInt(e.target.value) || 100)}
                min={1}
                max={999}
              />
            </div>
            <div className="admin-form-group">
              <label>Time Limit (seconds)</label>
              <input
                type="number"
                className="admin-form-input"
                value={timeLimit}
                onChange={(e) => setTimeLimit(parseInt(e.target.value) || 10)}
                min={1}
                max={60}
              />
            </div>
            <div className="admin-form-group">
              <label>Cards Per Session</label>
              <input
                type="number"
                className="admin-form-input"
                value={cardsPerSession}
                onChange={(e) => setCardsPerSession(parseInt(e.target.value) || 20)}
                min={1}
                max={50}
              />
            </div>
            <div className="admin-form-group">
              <label>Grade Range</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <select
                  className="admin-form-input"
                  value={minGrade}
                  onChange={(e) => setMinGrade(parseInt(e.target.value))}
                  style={{ flex: 1 }}
                >
                  {gradeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <span>to</span>
                <select
                  className="admin-form-input"
                  value={maxGrade}
                  onChange={(e) => setMaxGrade(parseInt(e.target.value))}
                  style={{ flex: 1 }}
                >
                  {gradeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="admin-modal-actions">
              <button className="admin-btn admin-btn-secondary" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-primary"
                onClick={handleSave}
                disabled={saving || !name.trim()}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm !== null && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <h3>Delete Category</h3>
            <p className="confirm-dialog-text">
              Are you sure you want to delete this category? This will also delete all its flash cards.
            </p>
            <div className="admin-modal-actions">
              <button className="admin-btn admin-btn-secondary" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-danger"
                onClick={handleDelete}
                disabled={saving}
              >
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;
